import type { ProductPlan } from "@/features/products/types"
import { formatMoney } from "@/shared/lib/money"
import { isTrialPlan, renewalPhrase, trialDurationPhrase } from "./pricing"

/** App brand shown in the legal charge disclaimer. */
const BRAND = "Live Palmistry"

/**
 * First-person opt-in consent disclaimer ("I accept that …"), used where the
 * checkbox/CTA captures the user's acknowledgement of the charge.
 *
 * Pure and brand-aware; it adapts to the plan shape with no hardcoded price,
 * trial length or billing period:
 *   - trial plan            → reduced intro price + post-trial rebill
 *   - non-trial recurring   → charged today, then renews each period
 *   - one-time / lifetime   → single charge, no recurring
 */
export function buildConsentDisclaimer(plan: ProductPlan): string {
  const today = formatMoney(plan.price, plan.currency)
  const phrase = renewalPhrase(plan)

  if (isTrialPlan(plan)) {
    const recurring = formatMoney(plan.recurringPrice ?? plan.price, plan.currency)
    const trialPhrase = trialDurationPhrase(plan)
    return `Your ${trialPhrase}trial will cost only ${today}. Without cancellation, before the selected discounted intro plan ends, I accept that ${BRAND} will automatically charge ${recurring}${phrase ? ` ${phrase}` : ""} until I cancel.`
  }

  // Non-trial recurring subscription (no reduced intro price).
  if (phrase) {
    return `You'll be charged ${today} today. Without cancellation, I accept that ${BRAND} will automatically charge ${today} ${phrase} until I cancel.`
  }

  // Non-trial one-time / lifetime purchase.
  return `You'll be charged ${today} today. This is a one-time payment — no recurring charges.`
}

/**
 * Third-person informational disclaimer covering renewal terms and the refund
 * policy, used on the standard checkout layout.
 *
 * Pure; adapts to the plan shape with no hardcoded price, trial length or
 * billing period:
 *   - trial plan            → trial starts today, renews at full price later
 *   - non-trial recurring   → charged today, then renews at the same price
 *   - one-time / lifetime    → single charge, no recurring
 */
export function buildRenewalDisclaimer(plan: ProductPlan): string {
  const phrase = renewalPhrase(plan)
  const price = formatMoney(plan.price, plan.currency)

  // Trial: charged a reduced price today, renews at the full recurring price later.
  if (isTrialPlan(plan)) {
    const recurring = formatMoney(plan.recurringPrice ?? plan.price, plan.currency)
    const renews = `${recurring}${phrase ? ` ${phrase}` : ""}`
    const trialPhrase = trialDurationPhrase(plan)
    return `Your ${trialPhrase}trial starts today. You can cancel anytime before it ends and won't be charged the full price. After the trial, your subscription renews automatically at ${renews} unless canceled. No refunds for unused time after renewal.`
  }

  // One-time / lifetime: no recurring charge.
  if (!phrase) {
    return `You'll be charged ${price} today. This is a one-time payment — no recurring charges.`
  }

  // Recurring (no trial): charged today and renews at the same price.
  return `You'll be charged ${price} today. Your subscription then renews automatically at ${price} ${phrase} unless canceled. You can cancel anytime. No refunds for unused time after renewal.`
}
